<script setup lang="ts">
import { usePersonalAccountDataState } from '../../../../utilities/account/useAccountDataPersonal'
import { ChevronLeftIcon, DocumentTextIcon } from '@heroicons/vue/outline'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'
import { ref } from 'vue'
import AccountPanelPersonalComplianceProfileCategory from '../complianceProfile/AccountPanelPersonalComplianceProfileCategory.vue'
import { esacPersonalComplianceProfileConfig } from '../complianceProfile/esacComplianceProfile'
import BaseHeader from '../../../base/BaseHeader.vue'
import { getPlatformSettings } from '../../../../utilities/usePlatformSettings'
import { ecspPersonalComplianceProfileConfig } from '../complianceProfile/ecspComplianceProfile'
import WizardInfoAlert from '../../../wizard/WizardInfoAlert.vue'
import { CheckIcon } from '@heroicons/vue/solid'
import { usePersonalAccountFormState } from '../../../../utilities/account/useAccountFormPersonal'
import { promiseHandler } from '../../../../utilities/helpers'
import { openConfirmDialog } from '../../../../utilities/useDialog'
import { i18n } from '../../../../localization'
import EcspStatus from '../../common/verification/EcspStatus.vue'
import { useOpenCloseForm, useSaveForm } from '../../../../utilities/helpers'

const { naturalProfile: account } = usePersonalAccountDataState()

const currentForm = ref(null)

const store = usePersonalAccountDataState()
const accountFormStore = usePersonalAccountFormState()

const complianceProfile =
    getPlatformSettings().compliance_profile === 'esac'
        ? esacPersonalComplianceProfileConfig
        : ecspPersonalComplianceProfileConfig

const model =
    getPlatformSettings().compliance_profile === 'esac'
        ? account.compliance_profiles.esac
        : account.compliance_profiles.ecsp

const infoTextKey =
    getPlatformSettings().compliance_profile === 'esac' ? 'esac.natural.info_text' : 'ecsp.natural.info_text'

function navigateToForm(key: string) {
    currentForm.value = key
}

async function saveCurrentForm() {
    if (currentForm.value === 'Financial Situation') {
        const confirm = await openConfirmDialog(
            i18n.global.t('compliance_profile.save_financial_data_header'),
            i18n.global.t('compliance_profile.save_financial_data_description'),
            {
                type: 'Warning',
                confirmText: 'Speichern',
            }
        )
        if (!confirm) {
            return
        }
    }

    const [data, error] = await promiseHandler(accountFormStore.submit())
    if (data) {
        currentForm.value = null
    }
    store.fetchAccountData()
}

const { closeForm } = useOpenCloseForm()
const handleCloseForm = () => {
    closeForm(model, currentForm, complianceProfile, accountFormStore)
}

const { saveForm } = useSaveForm()
const handleSaveForm = () => {
    saveForm(currentForm, complianceProfile, accountFormStore)
}
</script>

<template>
    <div>
        <div v-if="currentForm === null">
            <WizardInfoAlert v-if="infoTextKey === 'ecsp.natural.info_text'">
                <i18n-t :keypath="infoTextKey" tag="p">
                    <template #financialSituationLink>
                        <u
                            ><a
                                class="cursor-pointer font-bold text-gray-700"
                                @click="navigateToForm('Financial Situation')"
                                >{{ $t('ecsp.natural.info_text_link_placeholder') }}</a
                            ></u
                        >
                    </template>
                    <template #newLine>
                        <br />
                    </template>
                    <template #highlight1>
                        <b>{{ $t('ecsp.natural.info_text_highlight_one') }}</b>
                    </template>
                    <template #highlight2>
                        <b>{{ $t('ecsp.natural.info_text_highlight_two') }}</b>
                    </template>
                    <template #highlight3>
                        <b>{{ $t('ecsp.natural.info_text_highlight_three') }}</b>
                    </template>
                </i18n-t>
            </WizardInfoAlert>
            <WizardInfoAlert v-if="infoTextKey === 'esac.natural.info_text'">
                <i18n-t :keypath="infoTextKey" tag="p" />
            </WizardInfoAlert>
            <div class="px-4 py-5 sm:px-6">
                <div class="overflow-hidden border border-gray-200 bg-white shadow sm:col-span-1 sm:rounded-md">
                    <ul role="list" class="ml-0 list-none divide-y divide-gray-200">
                        <AccountPanelPersonalComplianceProfileCategory
                            v-for="(form, key) in complianceProfile"
                            :key="key"
                            :form="form"
                            :page-key="key"
                            @navigate-to-form="navigateToForm" />
                    </ul>
                </div>
            </div>
        </div>

        <div v-else>
            <div class="py-4">
                <BaseButtonMuted class="mx-4 font-bold sm:mx-6" :icon="ChevronLeftIcon" @click="handleCloseForm">
                    {{ $t('compliance_profile.back_to_compliance_profile') }}
                </BaseButtonMuted>
            </div>
            <BaseHeader
                class="border-b border-t py-2.5"
                :title="complianceProfile[currentForm].title"
                :icon="DocumentTextIcon">
                <EcspStatus
                    v-if="currentForm === 'Investor Classification'"
                    :ecsp-status="store.naturalProfile.ecsp_status" />
            </BaseHeader>
            <div class="px-4 pt-3 sm:px-6">
                <component
                    :is="complianceProfile[currentForm].component"
                    v-model="model[complianceProfile[currentForm].validationKey]" />
            </div>
        </div>
        <div
            v-if="currentForm !== null"
            class="mt-3 justify-end space-y-4 border-t border-gray-300 px-4 py-5 pt-6 sm:flex sm:space-y-0 sm:px-6">
            <BaseButtonSecondary
                :icon="CheckIcon"
                size="lg"
                :loading="store.saving"
                :disabled="store.saving || accountFormStore.hasValidationErrors"
                @click="handleSaveForm">
                {{ $t('account.save') }}
            </BaseButtonSecondary>
        </div>
    </div>
</template>
